import { motion } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { CONTACT } from "@/i18n/translations";

const HERO_IMG =
  "https://images.unsplash.com/photo-1501258480117-ddc2b0447dab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxicmlnaHQlMjBsaWdodCUyMHdvb2QlMjB0ZXh0dXJlJTIwYmFja2dyb3VuZCUyMHdoaXRlfGVufDB8fHx8MTc5MDYxMzQ2N3ww&ixlib=rb-4.1.0&q=85";

export const SimpleHero = () => {
  const { t } = useLanguage();
  const wa = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(t.whatsapp.message)}`;

  return (
    <section id="top" className="relative overflow-hidden bg-white" data-testid="hero-section">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Light wood texture" className="w-full h-full object-cover" data-testid="hero-bg-image" />
        <div className="absolute inset-0 bg-white/90" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/55 via-white/30 to-white" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 pt-44 pb-24 lg:pt-52 lg:pb-32">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="eyebrow-label flex items-center gap-3"
        >
          <span className="inline-block w-10 h-px bg-[#14703F]" />
          {t.mini.heroEyebrow}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif-display tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl mt-7 text-[#12261A]"
          data-testid="hero-headline"
        >
          {t.mini.heroTitleA}
          <br />
          <span className="italic text-[#14703F]">{t.mini.heroTitleB}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35 }}
          className="mt-7 max-w-xl text-sm sm:text-base text-[#51685A] leading-relaxed"
          data-testid="hero-subcopy"
        >
          {t.mini.heroSub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="mt-10 flex flex-wrap gap-3"
        >
          <a
            href="#products"
            data-testid="hero-products-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#14703F] text-white px-7 py-4 font-mono-spec text-[11px] tracking-[0.2em] uppercase font-semibold hover:bg-[#0E5A32] transition-colors duration-300"
          >
            {t.mini.viewProducts}
            <ArrowDown size={14} />
          </a>
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            className="inline-flex items-center gap-2 rounded-full border border-[rgba(20,112,63,0.4)] text-[#14703F] px-7 py-4 font-mono-spec text-[11px] tracking-[0.2em] uppercase hover:bg-[rgba(20,112,63,0.07)] transition-colors duration-300"
          >
            <MessageCircle size={14} />
            {t.mini.whatsapp}
          </a>
        </motion.div>
      </div>
    </section>
  );
};
